import { Image, Upload } from "antd";
import { useState } from "react";
import { Controller, Control } from "react-hook-form";
import type { GetProp, UploadFile, UploadProps } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import FileToBase64 from "helpers/fileToBase64";
import { RcFile, UploadChangeParam } from "antd/es/upload";
import { useNotificationContext } from "contexts/NotificationContext";

interface IProps {
  label: string;
  name: string;
  control: Control<any, any>;
  placeholder?: string;
  action: (file: RcFile) => Promise<string | undefined>;
  onRemove?: (url?: string) => void;
  maxSizeMB?: number;
}

type FileType = Parameters<GetProp<UploadProps, "beforeUpload">>[0];

export default function LabeledUploadInput({
  label,
  control,
  name,
  placeholder,
  action,
  onRemove,
  maxSizeMB = 5,
}: IProps) {
  const { showError, showSuccess } = useNotificationContext();
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewImage, setPreviewImage] = useState("");

  const handlePreview = async (file: UploadFile) => {
    if (!file.url && !file.preview) {
      file.preview = await FileToBase64(file.originFileObj as FileType);
    }
    setPreviewImage(file.url || (file.preview as string));
    setPreviewOpen(true);
  };

  const handleBeforeUpload = (file: RcFile) => {
    if (!file.type.startsWith("image/")) {
      showError("Only image files are allowed.");
      return false;
    }

    if (file.size > maxSizeMB * 1024 * 1024) {
      showError(`File size exceeds ${maxSizeMB}MB limit.`);
      return false;
    }

    return true;
  };

  const customRequest = async ({
    file,
    onSuccess,
    onError,
    onProgress,
    onChange,
  }: any) => {
    try {
      const rcFile = file as RcFile;

      // Simulate progress (replace with actual progress if your action supports it)
      let progress = 0;
      const progressInterval = setInterval(() => {
        progress += 10;
        onProgress({ percent: progress });
        if (progress >= 90) clearInterval(progressInterval);
      }, 200);

      const fileUrl = await action(rcFile);

      clearInterval(progressInterval);
      onProgress({ percent: 100 });

      onSuccess({ url: fileUrl }, rcFile);
      if (fileUrl) onChange([{ url: fileUrl, name: rcFile.name }]);
    } catch (error) {
      onError(new Error("Upload failed"));
    }
  };

  return (
    <div className="w-full flex flex-col gap-y-1">
      <label htmlFor={name} className="text-gray-600 font-medium text-sm">
        {label}
      </label>
      <Controller
        control={control}
        name={name}
        render={({
          field: { value, onChange, disabled, name },
          fieldState: { error },
        }) => {
          const handleChange: UploadProps["onChange"] = async (
            info: UploadChangeParam<UploadFile>
          ) => {
            const { fileList } = info;
            // Update the form field value
            onChange(fileList);
            const file = info.file;

            if (file.status === "uploading") {
              return;
            }

            if (file.status === "done")
              showSuccess(`${file.name} uploaded successfully`);

            if (file.status === "error")
              showError(`${file.name} upload failed.`);
          };

          return (
            <>
              <Upload
                name={name}
                className="w-full multiple-images-selector min-w-40"
                rootClassName="min-w-40"
                listType="picture-card"
                fileList={value ?? []}
                // ref={ref}
                disabled={disabled}
                accept="image/*"
                onRemove={(file: UploadFile<any>) => {
                  onRemove?.(file.response?.url);
                  onChange(null);
                }}
                beforeUpload={handleBeforeUpload}
                onPreview={handlePreview}
                onChange={handleChange}
                customRequest={(options) =>
                  customRequest({ ...options, onChange })
                }
                showUploadList={{
                  showRemoveIcon: true,
                  showPreviewIcon: true,
                  showDownloadIcon: false,
                }}
              >
                {value?.length >= 1 ? null : (
                  <button
                    style={{ border: 0, background: "none" }}
                    type="button"
                    className="min-w-40"
                  >
                    <PlusOutlined />
                    <div className="text-sm" style={{ marginTop: 8 }}>
                      {placeholder ?? "Upload"}
                    </div>
                  </button>
                )}
              </Upload>
              <span className="text-xs text-red-500">{error?.message}</span>
            </>
          );
        }}
      />
      {previewImage && (
        <Image
          wrapperStyle={{ display: "none" }}
          preview={{
            visible: previewOpen,
            onVisibleChange: (visible) => setPreviewOpen(visible),
            afterOpenChange: (visible) => !visible && setPreviewImage(""),
          }}
          src={previewImage}
        />
      )}
    </div>
  );
}
